import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useTheme } from 'next-themes';

interface DistributionData {
  name: string;
  count: number;
  color: string;
}

interface DistributionPieChartProps {
  data: DistributionData[];
  height?: number;
  className?: string;
  title?: string;
  emptyStateIcon?: React.ComponentType<{ className?: string }>;
  emptyStateMessage?: string;
  onSliceClick?: (sliceName: string, count: number) => void;
}

export const DistributionPieChart: React.FC<DistributionPieChartProps> = ({
  data,
  height = 192, 
  className = '',
  title,
  emptyStateIcon: EmptyStateIcon,
  emptyStateMessage = "No data available",
  onSliceClick
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (!chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    chartInstance.current = echarts.init(chartRef.current, theme === 'dark' ? 'dark' : '');

    const hasData = data && data.some(item => item.count > 0);

    if (!hasData) {
      chartInstance.current.clear();
      return;
    }

    const option = {
      backgroundColor: 'transparent',
      title: title ? {
        text: title,
        left: 'center',
        top: '10%',
        textStyle: {
          fontSize: 14,
          fontWeight: 'normal'
        }
      } : undefined,
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      legend: {
        show: false
      },
      series: [
        {
          type: 'pie',
          radius: ['40%', '80%'],
          center: ['50%', '50%'],
          data: data.map(item => ({
            value: item.count,
            name: item.name,
            itemStyle: {
              color: item.color
            }
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          label: {
            show: false
          }
        }
      ]
    };

    chartInstance.current.setOption(option);

    if (onSliceClick) {
      chartInstance.current.off('click'); 
      chartInstance.current.on('click', (params: any) => {
        if (params.componentType === 'series') {
          onSliceClick(params.data.name, params.data.value);
        }
      });
    }

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [data, title, theme, onSliceClick]);

  const hasData = data && data.some(item => item.count > 0);

  if (!hasData) {
    return (
      <div className={`flex items-center justify-center text-gray-500 ${className}`} style={{ height }}>
        <div className="text-center">
          {EmptyStateIcon && <EmptyStateIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />}
          <p>{emptyStateMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={chartRef} 
      className={`w-full ${className}`}
      style={{ height }}
    />
  );
};

export default DistributionPieChart;